import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ChevronDown } from "lucide-react";
import Reveal from "@/components/Reveal";
import { BOOKS } from "@/lib/site";

const API = process.env.REACT_APP_BACKEND_URL;

const INITIAL = {
  contactName: "",
  organization: "",
  email: "",
  phone: "",
  titles: "",
  estimatedQuantity: "",
  deliveryLocation: "",
  timeframe: "",
  message: "",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const labelCls = "block text-sm font-medium text-navy";
const inputCls =
  "mt-2 w-full bg-white border border-line-strong rounded-md px-4 py-3 text-[15px] text-ink placeholder:text-mist/70 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors";

export default function GroupCopies() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [done, setDone] = useState(false);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: "" });
  };

  const validate = () => {
    const next = {};
    if (!form.contactName.trim())
      next.contactName = "Please share your name.";
    if (!form.organization.trim())
      next.organization = "Please share your organization.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "Please share a valid email address.";
    if (!form.titles.trim())
      next.titles = "Please share the title or titles.";
    if (!form.estimatedQuantity.trim())
      next.estimatedQuantity = "Please estimate how many copies you need.";
    if (!form.deliveryLocation.trim())
      next.deliveryLocation = "Please share a delivery location.";
    if (!form.timeframe.trim())
      next.timeframe = "Please share your desired timeframe.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/bulk-inquiries`, form, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.data && res.data.ok) {
        setDone(true);
        window.__lenis
          ? window.__lenis.scrollTo(0, { immediate: true })
          : window.scrollTo(0, 0);
      } else {
        setServerError("Your request could not be saved. Please try again.");
      }
    } catch (err) {
      setServerError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const titleOptions = BOOKS.map((b) => b.title);

  return (
    <div className="bg-ivory">
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-28 sm:pt-44">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Group copies
              </p>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-navy tracking-tight leading-[1.1]">
                Put the book in many hands.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-stone2">
                Hospices, hospitals, senior living communities, churches,
                caregiver groups, and other organizations can request copies
                in quantity — for staff, volunteers, families, and the people
                they serve. Tell us what you need, and we&rsquo;ll take it from
                there.
              </p>

              <div className="mt-10 space-y-5">
                {[
                  "Your request is saved and read — every one is.",
                  "We’ll review your request and reply to the email you share to discuss a quote or next steps.",
                  "Titles, quantities, delivery, and timing are settled together.",
                ].map((s, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="shrink-0 font-serif text-gold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-stone2 leading-relaxed">{s.t ?? s}</p>
                  </div>
                ))}
              </div>

              <p className="mt-10 text-xs leading-relaxed text-mist border-t border-line pt-6">
                Submitting this form requests a quote or a conversation. It
                does not place an order, and no payment is collected on this
                site. Your request is kept private and used to respond to
                your inquiry.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {done ? (
                <div
                  className="bg-white border border-line rounded-md p-10 sm:p-14 text-center"
                  data-testid="bulk-success-message"
                  role="status"
                >
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-light">
                    <CheckCircle2 className="w-8 h-8 text-gold-hover" />
                  </span>
                  <h2 className="mt-7 font-serif text-3xl text-navy">
                    Received, with thanks.
                  </h2>
                  <p className="mt-4 text-stone2 leading-relaxed max-w-md mx-auto">
                    Your request for group copies has been saved and will be
                    read with care. Any reply goes to the email you shared —
                    nothing has been ordered and no payment has been
                    collected.
                  </p>
                  <Link
                    to="/books"
                    data-testid="bulk-success-books-link"
                    className="mt-9 inline-flex items-center gap-2 border border-navy/25 text-navy px-7 py-3 rounded-full text-sm font-medium hover:border-gold hover:text-gold-hover transition-colors"
                  >
                    Back to the books
                  </Link>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  noValidate
                  className="bg-white border border-line rounded-md p-8 sm:p-10 space-y-7"
                >
                  <div className="grid sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="bc-name" className={labelCls}>
                        Contact name <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="bc-name"
                        type="text"
                        value={form.contactName}
                        onChange={set("contactName")}
                        data-testid="bulk-input-contact-name"
                        className={inputCls}
                        placeholder="First and last"
                      />
                      {errors.contactName && (
                        <p className="mt-2 text-xs text-burgundy" role="alert">
                          {errors.contactName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="bc-org" className={labelCls}>
                        Organization <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="bc-org"
                        type="text"
                        value={form.organization}
                        onChange={set("organization")}
                        data-testid="bulk-input-organization"
                        className={inputCls}
                        placeholder="e.g., a hospice, a church, a care network"
                      />
                      {errors.organization && (
                        <p className="mt-2 text-xs text-burgundy" role="alert">
                          {errors.organization}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="bc-email" className={labelCls}>
                        Email address <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="bc-email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        data-testid="bulk-input-email"
                        className={inputCls}
                        placeholder="you@example.org"
                      />
                      {errors.email && (
                        <p className="mt-2 text-xs text-burgundy" role="alert">
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="bc-phone" className={labelCls}>
                        Phone <span className="text-mist">(optional)</span>
                      </label>
                      <input
                        id="bc-phone"
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        data-testid="bulk-input-phone"
                        className={inputCls}
                        placeholder="If a call is easier"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="bc-titles" className={labelCls}>
                      Title or titles <span className="text-burgundy">*</span>
                    </label>
                    <p className="mt-1 text-xs text-mist">
                      Titles you&rsquo;re interested in:{" "}
                      {titleOptions.join(" · ")}
                    </p>
                    <textarea
                      id="bc-titles"
                      rows={2}
                      value={form.titles}
                      onChange={set("titles")}
                      data-testid="bulk-textarea-titles"
                      className={`${inputCls} resize-y`}
                      placeholder="e.g., From Diagnosis to Goodbye and the Hospice Workbook"
                    />
                    {errors.titles && (
                      <p className="mt-2 text-xs text-burgundy" role="alert">
                        {errors.titles}
                      </p>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="bc-qty" className={labelCls}>
                        Estimated quantities <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="bc-qty"
                        type="text"
                        value={form.estimatedQuantity}
                        onChange={set("estimatedQuantity")}
                        data-testid="bulk-input-quantity"
                        className={inputCls}
                        placeholder="e.g., 25 of the main book, 40 workbooks"
                      />
                      {errors.estimatedQuantity && (
                        <p className="mt-2 text-xs text-burgundy" role="alert">
                          {errors.estimatedQuantity}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="bc-time" className={labelCls}>
                        Desired timeframe <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="bc-time"
                        type="text"
                        value={form.timeframe}
                        onChange={set("timeframe")}
                        data-testid="bulk-input-timeframe"
                        className={inputCls}
                        placeholder="e.g., by early October, before a fall program"
                      />
                      {errors.timeframe && (
                        <p className="mt-2 text-xs text-burgundy" role="alert">
                          {errors.timeframe}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="bc-delivery" className={labelCls}>
                      Delivery location <span className="text-burgundy">*</span>
                    </label>
                    <input
                      id="bc-delivery"
                      type="text"
                      value={form.deliveryLocation}
                      onChange={set("deliveryLocation")}
                      data-testid="bulk-input-delivery"
                      className={inputCls}
                      placeholder="City and state, or the delivery address"
                    />
                    {errors.deliveryLocation && (
                      <p className="mt-2 text-xs text-burgundy" role="alert">
                        {errors.deliveryLocation}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="bc-message" className={labelCls}>
                      Anything else <span className="text-mist">(optional)</span>
                    </label>
                    <textarea
                      id="bc-message"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      data-testid="bulk-textarea-message"
                      className={`${inputCls} resize-y`}
                      placeholder="The occasion, who the copies are for, questions — anything helpful."
                    />
                  </div>

                  {serverError && (
                    <p
                      className="text-sm text-burgundy bg-burgundy-soft border border-burgundy/20 rounded-md px-4 py-3"
                      role="alert"
                      data-testid="bulk-server-error"
                    >
                      {serverError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="bulk-submit-button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-navy text-ivory px-9 py-4 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200 disabled:opacity-60 disabled:cursor-wait"
                  >
                    {submitting ? "Sending…" : "Request a quote"}
                  </button>
                  <p className="text-xs text-mist">
                    This requests a quote or conversation — it does not place
                    an order, and no payment is collected here.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
